/**
 * Shared by truckInspectionRenewal.js and storageFacilityInspectionRenewal.js — the
 * frontend mirror of the backend's RenewableInspectionReport concern. Both report
 * types are "complete once, valid for 1 year, renewable in a 21-day grace window
 * before expiry" and differ only in which field holds the completion date.
 */

const OVERLAP_GRACE_DAYS = 21;
const INSPECTION_VALIDITY_YEARS = 1;
const MS_PER_DAY = 86400000;

export const parseDateOnly = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

export const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date, days) => new Date(date.getTime() + days * MS_PER_DAY);

const addYears = (date, years) => new Date(date.getFullYear() + years, date.getMonth(), date.getDate());

const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const periodsOverlap = (date, expiresOn, existingDate, existingExpiresOn) =>
  date < existingExpiresOn && expiresOn > existingDate;

export const formatRenewalDate = (isoDate) => {
  const date = parseDateOnly(isoDate);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Builds the renewal-window/status helpers for one report type, keyed off the field holding its completion date. */
export const createRenewalHelpers = (dateField) => {
  const inspectionExpiresOnIso = (dateValue) => {
    const date = parseDateOnly(dateValue);
    if (!date) return null;
    return toIsoDate(addYears(date, INSPECTION_VALIDITY_YEARS));
  };

  const inspectionExpiresOn = (report) => {
    if (!report) return null;
    if (report.expires_on) return report.expires_on;

    return inspectionExpiresOnIso(report[dateField]);
  };

  const getEarliestAllowedDate = (reports, referenceDate = new Date()) => {
    const completed = (reports || []).filter(
      (report) => report.status === 'completed' && report[dateField]
    );
    const reference = startOfDay(referenceDate);

    const graceStarts = completed
      .map((existing) => {
        const existingDate = parseDateOnly(existing[dateField]);
        const existingExpiresOn = parseDateOnly(inspectionExpiresOn(existing));
        if (!existingDate || !existingExpiresOn || reference >= existingExpiresOn) {
          return null;
        }

        const newExpiresOn = addYears(reference, INSPECTION_VALIDITY_YEARS);
        if (!periodsOverlap(reference, newExpiresOn, existingDate, existingExpiresOn)) {
          return null;
        }

        return addDays(existingExpiresOn, -OVERLAP_GRACE_DAYS);
      })
      .filter(Boolean);

    if (!graceStarts.length) return null;

    return graceStarts.reduce((latest, date) => (date > latest ? date : latest));
  };

  const canCreateNewInspectionReport = (reports, referenceDate = new Date()) => {
    const earliestDate = getEarliestAllowedDate(reports, referenceDate);
    if (!earliestDate) {
      return { allowed: true, earliestAllowedDate: null, message: null };
    }

    const earliestAllowedDate = toIsoDate(earliestDate);
    const reference = startOfDay(referenceDate);

    if (reference >= earliestDate) {
      return { allowed: true, earliestAllowedDate, message: null };
    }

    return {
      allowed: false,
      earliestAllowedDate,
      message: `Next inspection can be created on ${formatRenewalDate(earliestAllowedDate)}.`,
    };
  };

  const isDateAllowed = (dateValue, earliestAllowedDate) => {
    if (!earliestAllowedDate || !dateValue) return true;
    return dateValue >= earliestAllowedDate;
  };

  const dateRenewalError = (earliestAllowedDate) =>
    `Inspection date must be on or after ${formatRenewalDate(earliestAllowedDate)}.`;

  const reportStatus = (report) => {
    if (report.status === 'draft') {
      return { label: 'Awaiting inspection', color: 'warning' };
    }

    const expiresOnIso = inspectionExpiresOn(report);
    if (!expiresOnIso) {
      return { label: 'Completed', color: 'success' };
    }

    const expiresOn = parseDateOnly(expiresOnIso);
    const today = startOfDay(new Date());

    if (!expiresOn) {
      return { label: 'Completed', color: 'success' };
    }

    if (expiresOn < today) {
      return { label: 'Expired', color: 'default' };
    }

    const daysUntilExpiry = Math.ceil((expiresOn - today) / MS_PER_DAY);
    if (daysUntilExpiry <= 30) {
      return { label: 'Expiring soon', color: 'warning' };
    }

    return { label: 'Active', color: 'success' };
  };

  const getInspectionAttentionStatus = (reports, referenceDate = new Date(), messages = {}) => {
    const list = reports || [];
    const hasDraft = list.some((report) => report.status === 'draft');
    const renewalStatus = canCreateNewInspectionReport(list, referenceDate);
    const renewalBlocked = !hasDraft && !renewalStatus.allowed;

    if (renewalBlocked) {
      return { show: false, message: null, variant: null };
    }

    const hasCompleted = list.some((report) => report.status === 'completed');

    if (hasDraft) {
      return { show: true, message: messages.draftAwaiting, variant: 'draft' };
    }

    if (!hasCompleted) {
      return { show: true, message: messages.noCompleted, variant: 'missing' };
    }

    const latestCompleted = list
      .filter((report) => report.status === 'completed' && report[dateField])
      .reduce((latest, report) => {
        if (!latest) return report;
        const reportDate = parseDateOnly(report[dateField]);
        const latestDate = parseDateOnly(latest[dateField]);
        if (!reportDate || !latestDate) return latest;
        return reportDate > latestDate ? report : latest;
      }, null);

    const expiresIso = latestCompleted ? inspectionExpiresOn(latestCompleted) : null;
    const referenceIso = toIsoDate(startOfDay(referenceDate));
    const isExpired = expiresIso && expiresIso < referenceIso;

    return {
      show: true,
      message: isExpired ? messages.expired : messages.renewalDue,
      variant: isExpired ? 'expired' : 'renewal_due',
    };
  };

  return {
    inspectionExpiresOnIso,
    inspectionExpiresOn,
    getEarliestAllowedDate,
    canCreateNewInspectionReport,
    isDateAllowed,
    dateRenewalError,
    reportStatus,
    getInspectionAttentionStatus,
  };
};
